/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2020-2021 (original work) Open Assessment Technologies SA ;
 */

/**
 * Gets deep property inside an object, if it exists.
 *
 * @param {Object} obj
 * @param {Array} properties
 *
 * @returns {*|undefined}
 */
export const getIn = (obj, properties = []) => {
    if (obj === null || typeof obj !== 'object') {
        return;
    }

    let buf = obj;
    for (const property of properties) {
        if (!buf[property]) {
            return void 0;
        }

        buf = buf[property];
    }

    return buf;
};

/**
 * Deeply compares two values
 *
 * @param {*} a - json-valid value
 * @param {*} b - json-valid value
 *
 * @returns {boolean}
 */
export const isEqual = (a, b) => a === b || JSON.stringify(a) === JSON.stringify(b);

/**
 * Converts array to key-value form (where the key is id).
 *
 * @param {Array<Object>} [data=[]] - data
 *
 * @returns {Object}
 */
export function keyById(data = []) {
    return data.reduce((res, o) => {
        res[o.id] = o;
        return res;
    }, {});
}

/**
 * Flatten specific keys to original object
 * @param {Object} obj
 * @param {string[]} keys
 * @returns {Object}
 */
export function flattenKeys(obj = {}, keys = []) {
    for (const key of keys) {
        // eslint-disable-next-line no-prototype-builtins
        if (obj.hasOwnProperty(key)) {
            const value = obj[key];
            if (typeof value === 'object' && !Array.isArray(value)) {
                obj = { ...obj, ...value };
                delete obj[key];
            } else {
                // eslint-disable-next-line no-console
                console.warn(`Value under key '${key}' is not an object and cannot be spread.`);
            }
        }
    }

    return obj;
}

/**
 * Retrieves the value from a nested object using a dot-separated string path.
 *
 * @param {Object} obj - The object to retrieve the value from.
 * @param {string} path - The dot-separated path to the desired value.
 * @returns {*} - The value at the specified path, or undefined if the path is invalid.
 */
export function getValueByPath(obj, path) {
    const keys = path.split('.');
    let value = obj;

    for (const key of keys) {
        if (value && typeof value === 'object' && key in value) {
            value = value[key];
        } else {
            return void 0; // Return undefined if the path is invalid
        }
    }
    return value;
}
